import React from 'react';
import {
  GruppertSkjema,
  OneWayInOpportunityDto,
  OpportunityEmailThreadDto,
  SendEmailToCustomerDto,
} from 'src/api/v2';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import useCustomerEmail from '../../hooks/use-customer-email';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ToastContent from 'src/components/ToastContent/ToastContent';
import { Button, Card, FaIcon, FaIcons, Textarea } from '@innovationnorway/component-library';
import { useCluster } from 'src/features/cluster';
import { minValue } from 'src/utils/validation';
import { useTranslation } from 'react-i18next';
import { useUser } from 'src/features/authorization';

type Props = {
  show: boolean;
  onClose: () => void;
  opportunity?: OneWayInOpportunityDto;
  groupedForm?: GruppertSkjema;
  thread?: OpportunityEmailThreadDto;
  formId?: string;
};

const CreateEmailThreadModal: React.FC<Props> = ({
  show,
  onClose,
  opportunity,
  groupedForm,
  thread,
  formId,
}) => {
  const { sendEmailMutation } = useCustomerEmail(opportunity?.numericId, formId);
  const { cluster } = useCluster();
  const { displayName } = useUser();
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const form = useForm<SendEmailToCustomerDto>();

  const handleOnSubmit = form.handleSubmit(async (input) => {
    if (!input.bodyText || !input.bodyText.length) {
      return;
    }

    const data = {
      recipientEmail: groupedForm!.kontakt!.epost!,
      threadId: thread ? thread.numericId : undefined,
      bodyText: input.bodyText,
      opportunityId: thread ? undefined : opportunity!.id,
    } satisfies SendEmailToCustomerDto;

    await sendEmailMutation
      .mutateAsync({ dto: data })

      .then(() => {
        toast(
          <ToastContent
            title={tCommon('emailSent')}
            text={tCommon('emailSentDetails', { email: data.recipientEmail })}
            success
          />,
          {
            toastId: 'NoteAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        );
        onClose();
      })
      .catch(() =>
        toast(
          <ToastContent title={tCommon('updateFailed')} text={tCommon('emailNotSent')} success={false} />,
          {
            toastId: 'NoteAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        ),
      );
  });

  return (
    <DialogContainer title={tOneWayIn('communication.sendEmailToCustomer')} show={show} onClose={onClose}>
      <form onSubmit={handleOnSubmit}>
        <Card
          className="email-preview padding--2 stack-medium font-size--medium owi-email-viewer__content"
          id="email-preview"
        >
          <div className="email-preview__header">
            <span className="font-size--x-large">
              <FaIcon className="margin-right--1" name={FaIcons.EnvelopeLight} /> Din henvendelse til Én Vei
              Inn - {opportunity?.numericId},{thread ? thread.numericId : '1000000'}
            </span>
          </div>
          <p>
            Hei {groupedForm?.kontakt?.fornavn} {groupedForm?.kontakt?.etternavn},
          </p>
          <p>Dette gjelder henvendelsen din {opportunity?.title}.</p>
          <Controller
            control={form.control}
            name="bodyText"
            rules={{
              minLength: minValue(30),
            }}
            render={({ field, fieldState }) => <Textarea {...field} errorMsg={fieldState.error?.message} />}
          />
          <p>
            Med vennlig hilsen <br />
            {displayName} <br />
            {cluster?.name}
          </p>
          <p>
            Du kan svare på denne e-posten, men <strong>ikke</strong> endre emnefeltet.
          </p>
        </Card>
        <Button
          type="submit"
          theme="neutral"
          variant="solid"
          isLoading={sendEmailMutation.isLoading}
          disabled={sendEmailMutation.isLoading}
        >
          {tCommon('send')}
        </Button>
      </form>
    </DialogContainer>
  );
};

export default CreateEmailThreadModal;
